<template>
  <div class="h-100">
    <v-chart ref="chart" :options="options" :echart.sync="echart" />
  </div>
</template>

<script>
import { format } from 'date-fns';
import { merge } from 'lodash';
import chartable from '@/mixins/chartable';
import bus from '@/utils/bus';

export default {
  mixins: [chartable],
  props: {
    settings: {
      type: Object,
      required: true,
    },
    motlet: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      options: {
        xAxis: {
          type: 'category',
          axisLabel: {
            formatter: value => format(parseInt(value), 'HH:mm:ss'),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        yAxis: {
          nameGap: 7,
          nameTextStyle: {
            fontWeight: 'normal',
            fontSize: 11,
            color: '#bbbbaa',
          },
          minInterval: 1,
          axisLabel: {
            inside: true,
            showMinLabel: false,
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        grid: {
          top: '5px',
          left: '18px',
          right: '11px',
          bottom: '16px',
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            z: 0,
            label: {
              show: true,
              formatter: params => format(parseInt(params.value), 'HH:mm:ss'),
            },
          },
          formatter: params => {
            const param = params[0];
            const contents = [];
            contents.push('<i class="v-icon notranslate mdi mdi-clock-time-four-outline mr-1" style="font-size: 12px; line-height: 10px"></i>');
            contents.push(format(parseInt(param.data[2]), 'yyyy-MM-dd HH:mm:ss'));
            const vm = this;
            params.forEach(p => {
              const inst = vm.settings.selectedDescendants.find(d => d.id == p.seriesId);
              if (inst) {
                contents.push('<br />');
                contents.push(vm.circle(inst.color));
                contents.push(`<span style="color:${inst.color};font-weight:900;">`);
                contents.push(inst.text);
                contents.push('</span> : ');
                contents.push(vm.settings.callback.tooltip.formatter(p));
              }
            });
            return contents.join('');
          },
        },
      },
      echart: null,
    };
  },
  watch: {
    echart() {
      const canvas = this.echart.getDom().getElementsByTagName('canvas')[0];
      canvas.addEventListener('click', event => {
        const data = this.echart.getOption().xAxis[0].data;
        const pixels = data.map((value, index) => this.echart.convertToPixel({ xAxisIndex: 0 }, index));
        const closestValue = pixels.reduce(function(prev, curr) {
          return Math.abs(curr - event.offsetX) < Math.abs(prev - event.offsetX) ? curr : prev;
        });
        const index = pixels.findIndex(value => Math.abs(value) === closestValue);
        bus.$emit('click:grid', data[index]);
      });
    },
    settings: {
      deep: true,
      handler() {
        /*
         * Added.by.KWShin.2024.08.31.
         * 조상 컴포넌트들에서 데이터가 넘어오는 과정에 의하여
         * settings가 두번 변하는데 처음 변할 때는 this.motlet.descendants의 정보는 바뀐 시스템 정보에 따라 갱신되는데
         * this.settings.chart.series는 미처 갱신되지 않은 상태이고 두번째 변할 때야 비로소 갱신이 된다.
         * 따라서, 두 개의 길이가 일치하지 않을 때는 가볍게 무시를 하고
         * 길이가 같을 때만 후속 루틴을 타게 한다.
         */
        if (this.motlet.descendants.length != this.settings.chart.series.length) return;

        const data = this.settings.chart.xAxis.data;
        const series = [];
        this.settings.chart.series.forEach(s => {
          const seriesInst = this.motlet.descendants.find(d => d.id == s.id);
          const instColor = seriesInst.color;
          const activeInst = this.settings.selectedDescendants.find(d => d.id == s.id);
          let active = activeInst ? true : false;
          series.push({
            type: 'line',
            itemStyle: {
              color: this.motlet.descendant.color,
            },
            lineStyle: {
              color: instColor,
              opacity: active ? 1 : 0.1,
            },
            markLine: {
              symbol: 'none',
              label: { show: false },
              lineStyle: {
                type: 'solid',
                color: '#b55a09',
                width: 1,
              },
              data: [{ xAxis: data[data.length - (this.settings.markLineSeparation ? this.settings.markLineSeparation : 4)].toString() }],
            },
            id: s.id,
            data: s.data,
          });
        });
        // Modified.by.KWShin.2024.08.31.
        // 이거 묘하다... options의 내역이 this.options 내역을 완전히 엎어치는 것이 아니라
        // options 내의 Array의 길이만크만 this.options 내의 Array를 엎어친다.
        // 그래서 merge를 이용하지 않고 직접 할당하였다. merge() 메소드의 기능이 원래 그런건가?
        // this.options = merge({}, this.options, options);
        // --------------------------------------------------------------------
        this.options.xAxis = merge({}, this.options.xAxis, this.settings.chart.xAxis);
        this.options.yAxis = merge({}, this.options.yAxis, this.settings.chart.yAxis);
        this.options.series = series;
      },
    },
  },
  created() {
    bus.$on('click:grid', this.drawMarkLine);
  },
  methods: {
    drawMarkLine(timestamp) {
      const data = this.echart.getOption().xAxis[0].data;
      const closest = data.reduce(function(prev, curr) {
        return Math.abs(curr - timestamp) < Math.abs(prev - timestamp) ? curr : prev;
      });
      this.options.series[0].markLine.data[0].xAxis = closest.toString();
    },
    initializeChartOptions() {
      this.options.xAxis.data = [];
      this.options.series.forEach(s => {
        s.data = [];
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
